import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PlusCircle,
  Trash2,
  Pencil,
  X,
  Check,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Users,
  Eye,
  EyeOff,
} from "lucide-react";
import { ExclamationTriangleFill, Search } from "react-bootstrap-icons";

const USERS_PER_PAGE = 10;
const API = process.env.REACT_APP_API_URL;
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const token = localStorage.getItem("adminToken");

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (u) =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userList = (res.data.users || res.data).map((u) => ({
        id: u._id || u.id,
        name: u.name || "Unknown",
        email: u.email || "No email",
      }));
      setUsers(userList);
    } catch (err) {
      showToast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 4000);
  };

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return showToast("Authentication required", "error");

    const url = editingUser
      ? `http://localhost:5000/api/admin/users/${editingUser.id}`
      : "http://localhost:5000/api/admin/users";

    const payload = editingUser && !formData.password
      ? { name: formData.name, email: formData.email }
      : formData;

    try {
      const res = editingUser
        ? await axios.put(url, payload, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.post(url, payload, { headers: { Authorization: `Bearer ${token}` } });

      if (editingUser) {
        setUsers((prev) =>
          prev.map((u) => (u.id === editingUser.id ? { ...res.data, id: editingUser.id } : u))
        );
        showToast("User updated successfully");
      } else {
        setUsers((prev) => [{ ...res.data, id: res.data._id || res.data.id }, ...prev]);
        showToast("User successfully added");
      }
      closeModal();
    } catch (err) {
      showToast(err.response?.data?.error || "Operation failed", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.user) return;
    try {
      await axios.delete(`http://localhost:5000/api/admin/users/${deleteModal.user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((prev) => prev.filter((u) => u.id !== deleteModal.user.id));
      showToast("User deleted");
      setDeleteModal({ open: false, user: null });
    } catch {
      showToast("Delete failed", "error");
    }
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "" });
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: "" });
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "" });
  };

  return (
    <>
      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[10000] animate-[slideIn_0.4s_ease-out] max-w-[90vw]">
          <div
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm sm:text-base
              ${toast.type === "success" ? "bg-emerald-600" : "bg-red-600"}`}
          >
            {toast.type === "success" ? <Check size={18} /> : <AlertTriangle size={18} />}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 pb-10">
        <div className="w-full px-3.5 xs:px-4 sm:px-6 lg:px-10 py-5 sm:py-7">
          {/* Header + Add Button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
            <h1 className="text-2xl xs:text-3xl font-bold text-gray-800 flex items-center gap-2.5">
              <Users size={28} className="text-rose-600" />
              Users Management
            </h1>

            <button
              onClick={openAddModal}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg shadow-md transition min-w-[160px] touch-manipulation"
            >
              <PlusCircle size={20} />
              Add New User
            </button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
              />
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchTerm ? "No users found matching your search" : "No users yet"}
            </div>
          ) : (
            <>
              {/* Desktop Table – hidden below ~640px */}
              <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3.5 text-left text-sm font-semibold text-gray-600">S.No</th>
                      <th className="px-5 py-3.5 text-left text-sm font-semibold text-gray-600">Name</th>
                      <th className="px-5 py-3.5 text-left text-sm font-semibold text-gray-600">Email</th>
                      <th className="px-5 py-3.5 text-center text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedUsers.map((user, idx) => (
                      <tr key={user.id} className="hover:bg-gray-50/70">
                        <td className="px-5 py-4 text-sm text-gray-700">
                          {(currentPage - 1) * USERS_PER_PAGE + idx + 1}
                        </td>
                        <td className="px-5 py-4 font-medium text-gray-900">{user.name}</td>
                        <td className="px-5 py-4 text-gray-700">{user.email}</td>
                        <td className="px-5 py-4">
                          <div className="flex justify-center gap-3">
                            <button
                              onClick={() => openEditModal(user)}
                              className="p-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition"
                              title="Edit"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => setDeleteModal({ open: true, user })}
                              className="p-2 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 transition"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards – shown below ~640px */}
              <div className="sm:hidden space-y-4">
                {paginatedUsers.map((user, idx) => (
                  <div
                    key={user.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-0.5">
                          {(currentPage - 1) * USERS_PER_PAGE + idx + 1}
                        </div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, user })}
                          className="p-2 bg-rose-50 text-rose-700 rounded-lg hover:bg-rose-100"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 break-all">{user.email}</div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-1">
                  <div className="text-sm text-gray-600 order-2 sm:order-1">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </div>

                  <div className="flex items-center gap-2 order-1 sm:order-2">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2.5 rounded-lg border disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    <span className="px-4 py-2.5 bg-rose-600 text-white rounded-lg font-medium min-w-[3rem] text-center">
                      {currentPage}
                    </span>

                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2.5 rounded-lg border disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed transition"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal – Add / Edit */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-md mx-auto transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-rose-600 to-rose-700 text-white p-5 sm:p-6 rounded-t-2xl">
              <h2 className="text-xl font-semibold">
                {editingUser ? "Edit User" : "Add New User"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {editingUser ? "New Password (optional)" : "Password"}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent pr-11"
                    required={!editingUser}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-rose-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col xs:flex-row gap-3 pt-4 justify-end">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition order-2 xs:order-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg shadow transition order-1 xs:order-2"
                >
                  {editingUser ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div
          className="fixed inset-0 bg-black/65 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteModal({ open: false, user: null })}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDeleteModal({ open: false, user: null })}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-red-100 flex items-center justify-center border-8 border-red-50">
              <ExclamationTriangleFill size={40} className="text-red-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">Delete User?</h2>
            <p className="text-gray-600 mb-6">
              <strong>{deleteModal.user?.name}</strong> will be permanently deleted.
            </p>

            <div className="flex flex-col xs:flex-row gap-3 justify-center">
              <button
                onClick={() => setDeleteModal({ open: false, user: null })}
                className="px-7 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-xl border border-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-7 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl shadow transition"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsersPage;